import React, { useEffect, useMemo, useRef } from "react";
import { createUseStyles } from "react-jss";
import { FocusPicker } from "image-focus";
import { useScrollytellTranslation } from "../Admin/scrollytellContext";
import { debounce, IconButton } from "@mui/material";
import { useClickOutside } from "./useClickOutside";
import { Close } from "@mui/icons-material";

const useStyles = createUseStyles(({ colors }) => ({
  imageFocusPicker: {
    top: 0,
    left: 0,
    display: "flex",
    position: "absolute !important",
    flexDirection: "column",
    background: "white",
    padding: "10px",
    zIndex: 1500,
    boxShadow: "-2px 3px 12px 0px rgba(0,0,0,0.75)",
    "& h3": {
      margin: "10px 0",
      color: colors.black,
    },
  },
  imageFocusImage: {},
  imageFocusPickerHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
}));

export const ImageFocusPicker = ({
  imageUrl,
  x = 0,
  y = 0,
  onFocusChanged,
  debounceTime = 200,
  className = "",
  onClose,
}) => {
  const t = useScrollytellTranslation();
  const classes = useStyles();
  const wrapperRef = useRef(null);
  const imageRef = useRef(null);
  const focusPickerRef = useRef(null);

  const onFocusChangedRef = useRef(onFocusChanged);
  onFocusChangedRef.current = onFocusChanged;

  const debouncedOnFocusChanged = useMemo(
    () =>
      debounce((newFocus) => {
        if (typeof onFocusChangedRef.current === "function") {
          onFocusChangedRef.current(newFocus);
        }
      }, debounceTime),
    [debounceTime]
  );

  const debouncedRef = useRef(debouncedOnFocusChanged);
  debouncedRef.current = debouncedOnFocusChanged;

  useEffect(() => {
    return () => {
      debouncedOnFocusChanged.clear?.();
    };
  }, [debouncedOnFocusChanged]);

  useEffect(() => {
    if (!focusPickerRef.current) {
      if (imageRef.current) {
        focusPickerRef.current = new FocusPicker(imageRef.current, {
          onChange: (newFocus) => {
            debouncedRef.current?.(newFocus);
          },
          focus: { x, y },
        });
      }
    } else if (typeof focusPickerRef.current.setFocus === "function") {
      focusPickerRef.current.setFocus({ x, y });
    }
  }, [x, y]);

  useEffect(() => {
    return () => {
      if (typeof focusPickerRef.current?.disable === "function") {
        focusPickerRef.current.disable();
      }
      focusPickerRef.current = null;
    };
  }, []);

  useClickOutside(wrapperRef, () => onClose?.());

  const imageSrc =
    typeof imageUrl === "string" &&
    (imageUrl.startsWith("http://") ||
      imageUrl.startsWith("https://") ||
      imageUrl.startsWith("/"))
      ? imageUrl
      : `https://ems.dimu.org/image/${imageUrl}`;

  return (
    <div
      className={`${classes.imageFocusPicker} ${className}`}
      ref={wrapperRef}
    >
      <div className={classes.imageFocusPickerHeader}>
        <h3>{t("imageFocusPickerTitle", "Sett bildefokus")}</h3>
        <IconButton onClick={() => onClose?.()} aria-label={t("close", "Lukk")}>
          <Close />
        </IconButton>
      </div>
      <div>
        <img
          alt="Focus picker"
          className={classes.imageFocusImage}
          ref={imageRef}
          src={imageSrc}
        />
      </div>
    </div>
  );
};
